import GalleryOrderAdmin from './GalleryOrderAdmin';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { pieceName } from '../shopItems';
import { defaultStock } from '../catalog';
import { useCatalog } from '../catalogContext';
import '../css/App.css';

const PRODUCT_CATEGORIES = ['Screenprint', 'Relief', 'Lithography', 'Intaglio', 'Eco Print'];

const emptyForm = (item) => ({
    price: item.price,
    stock: defaultStock(item),
    bestSeller: item.badge === 'Best Seller' || item.bestSeller === true,
    edition: item.format === 'sticker' ? '' : (item.edition || ''),
    shopOrder: item.shopOrder ?? 0,
});

const blankProduct = () => ({
    title: '',
    year: '',
    category: 'Screenprint',
    format: '',
    price: '',
    stock: 0,
    shopOrder: '',
    edition: '',
    detail: '',
    summary: '',
    bestSeller: false,
    imagesData: [],
});

const Admin = () => {
    const { allItems, reserved, refresh } = useCatalog();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [user, setUser] = useState(null);
    const [checking, setChecking] = useState(true);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [adding, setAdding] = useState(false);
    const [added, setAdded] = useState('');
    const [draft, setDraft] = useState({});
    const [product, setProduct] = useState(blankProduct);
    const [imageKey, setImageKey] = useState(0);

    useEffect(() => {
        document.title = 'Admin · Delaney Stewart';
        const meta = document.querySelector('meta[name="robots"]') || document.createElement('meta');
        meta.setAttribute('name', 'robots');
        meta.setAttribute('content', 'noindex, nofollow');
        if (!meta.parentNode) {
            document.head.appendChild(meta);
        }
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        fetch('/api/admin/session', { credentials: 'include' })
            .then((response) => (response.ok ? response.json() : Promise.reject()))
            .then((data) => setUser(data.email))
            .catch(() => setUser(null))
            .finally(() => setChecking(false));
    }, []);

    useEffect(() => {
        const next = {};
        allItems.forEach((item) => {
            next[item.id] = emptyForm(item);
        });
        setDraft(next);
    }, [allItems]);

    const rows = useMemo(
        () => allItems.map((item) => ({ item, form: draft[item.id] || emptyForm(item) })),
        [allItems, draft]
    );
    const liveCount = rows.filter(({ item }) => !item.hidden).length;
    const soldCount = rows.filter(({ item, form }) => !item.hidden && Number(form.stock) <= 0).length;

    const updateRow = (id, field, value) => {
        setSaved(false);
        setDraft((prev) => ({
            ...prev,
            [id]: {
                ...(prev[id] || {}),
                [field]: value,
            },
        }));
    };

    const updateProduct = (field, value) => {
        setAdded('');
        setProduct((prev) => ({ ...prev, [field]: value }));
    };

    const onImages = (event) => {
        const files = Array.from(event.target.files || []).slice(0, 8);
        if (!files.length) {
            return;
        }
        Promise.all(files.map((file) => new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result || ''));
            reader.readAsDataURL(file);
        }))).then((imagesData) => {
            setAdded('');
            setProduct((prev) => ({
                ...prev,
                imagesData: [...(prev.imagesData || []), ...imagesData].slice(0, 8),
            }));
        });
    };

    const moveImage = (index, direction) => {
        setProduct((prev) => {
            const imagesData = [...(prev.imagesData || [])];
            const next = index + direction;
            if (next < 0 || next >= imagesData.length) {
                return prev;
            }
            [imagesData[index], imagesData[next]] = [imagesData[next], imagesData[index]];
            return { ...prev, imagesData };
        });
    };

    const removeImage = (index) => {
        setProduct((prev) => ({
            ...prev,
            imagesData: (prev.imagesData || []).filter((_, i) => i !== index),
        }));
    };

    const onLogin = (event) => {
        event.preventDefault();
        setError('');
        fetch('/api/admin/login', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
        })
            .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
            .then(({ ok, data }) => {
                if (!ok) {
                    setError(data.error || 'Could not sign in');
                    return;
                }
                setUser(data.email);
                setPassword('');
            })
            .catch(() => setError('Could not sign in'));
    };

    const onLogout = () => {
        fetch('/api/admin/logout', { method: 'POST', credentials: 'include' })
            .finally(() => setUser(null));
    };

    const onAddProduct = (event) => {
        event.preventDefault();
        setAdding(true);
        setError('');
        setAdded('');
        fetch('/api/admin/products', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                title: product.title,
                year: product.year,
                category: product.category,
                format: product.format,
                price: product.price,
                stock: Number(product.stock),
                shopOrder: product.shopOrder === '' ? undefined : Number(product.shopOrder),
                edition: product.format.toLowerCase() === 'sticker' ? '' : product.edition,
                detail: product.detail,
                summary: product.summary,
                bestSeller: Boolean(product.bestSeller),
                imagesData: product.imagesData,
            }),
        })
            .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
            .then(({ ok, data }) => {
                if (!ok) {
                    setError(data.error || 'Could not add product');
                    return;
                }
                setProduct(blankProduct());
                setImageKey((key) => key + 1);
                setAdded(data.product?.id ? `/products/${data.product.id}` : '/shop');
                refresh();
            })
            .catch(() => setError('Could not add product'))
            .finally(() => setAdding(false));
    };

    const onDelete = (item) => {
        const { name } = pieceName(item, { shop: true });
        const message = item.custom
            ? `Remove ${name} from the shop? This cannot be undone.`
            : `Remove ${name} from the shop? You can restore it later from this page.`;
        if (!window.confirm(message)) {
            return;
        }
        setError('');
        fetch(`/api/admin/products/${encodeURIComponent(item.id)}`, {
            method: 'DELETE',
            credentials: 'include',
        })
            .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
            .then(({ ok, data }) => {
                if (!ok) {
                    setError(data.error || 'Could not remove product');
                    return;
                }
                refresh();
            })
            .catch(() => setError('Could not remove product'));
    };

    const onRestore = (item) => {
        setError('');
        fetch(`/api/admin/products/${encodeURIComponent(item.id)}/restore`, {
            method: 'POST',
            credentials: 'include',
        })
            .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
            .then(({ ok, data }) => {
                if (!ok) {
                    setError(data.error || 'Could not restore product');
                    return;
                }
                refresh();
            })
            .catch(() => setError('Could not restore product'));
    };

    const onSave = (event) => {
        event.preventDefault();
        setSaving(true);
        setError('');
        setSaved(false);
        const overrides = {};
        Object.entries(draft).forEach(([id, row]) => {
            overrides[id] = {
                price: Number(row.price),
                stock: Number(row.stock),
                bestSeller: Boolean(row.bestSeller),
                edition: row.edition,
                shopOrder: Number(row.shopOrder),
            };
        });
        fetch('/api/admin/catalog', {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ overrides }),
        })
            .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
            .then(({ ok, data }) => {
                if (!ok) {
                    setError(data.error || 'Could not save');
                    return;
                }
                setSaved(true);
                refresh();
            })
            .catch(() => setError('Could not save'))
            .finally(() => setSaving(false));
    };

    return (
        <div className="storefront adminPage">
            <header className="adminChrome">
                <Link className="adminLogo" to="/">
                    <img src="/images/logo.png" alt="Delaney Stewart" />
                </Link>
                {user ?
                    <div className="adminChromeMeta">
                        <span className="adminChromeUser">{user}</span>
                        <Link className="adminGhostButton" to="/shop">View shop</Link>
                        <button className="adminTextButton" type="button" onClick={onLogout}>Sign out</button>
                    </div>
                    : null}
            </header>
            <main className="adminMain">
                <header className="adminHero">
                    <p className="adminKicker">Private</p>
                    <h1 className="adminTitle">Studio admin</h1>
                    <p className="adminLead">Prices, stock, photos, and shop order — all in one place.</p>
                </header>
                {checking ? <p className="adminStatus">Checking sign-in…</p> : null}
                {!checking && !user ?
                    <form className="adminLogin" onSubmit={onLogin}>
                        <h2 className="adminSectionTitle">Sign in</h2>
                        <p className="adminSectionCopy">Use the studio email and password from the server.</p>
                        <label>
                            Email
                            <input
                                type="email"
                                autoComplete="username"
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                                required
                            />
                        </label>
                        <label>
                            Password
                            <input
                                type="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                                required
                            />
                        </label>
                        {error ? <p className="adminError">{error}</p> : null}
                        <button className="adminButton" type="submit">Sign in</button>
                    </form>
                    : null}
                {!checking && user ?
                    <>
                        {error ? <p className="adminBanner adminBannerError">{error}</p> : null}

                        <GalleryOrderAdmin />
                        <form className="adminPanel adminProduct" onSubmit={onAddProduct}>
                            <div className="adminPanelHead">
                                <div>
                                    <h2 className="adminSectionTitle">Add a product</h2>
                                    <p className="adminSectionCopy">
                                        New pieces land in the shop sold out until you set stock. Up to 8 photos; first is the cover.
                                    </p>
                                </div>
                            </div>
                            <div className="adminProductGrid">
                                <div className="adminProductImage">
                                    <span className="adminFieldLabel">Photos</span>
                                    <div className="adminImageList">
                                        {(product.imagesData || []).map((src, index) => (
                                            <div className="adminImageThumb" key={`${src.slice(-24)}-${index}`}>
                                                <img src={src} alt="" />
                                                <div className="adminImageThumbActions">
                                                    <button type="button" onClick={() => moveImage(index, -1)} disabled={index === 0}>Up</button>
                                                    <button type="button" onClick={() => moveImage(index, 1)} disabled={index === product.imagesData.length - 1}>Down</button>
                                                    <button type="button" onClick={() => removeImage(index)}>Remove</button>
                                                </div>
                                                {index === 0 ? <span className="adminImagePrimary">Cover</span> : null}
                                            </div>
                                        ))}
                                        {!product.imagesData?.length ?
                                            <span className="adminImagePlaceholder">JPEG, PNG, WebP, or GIF · 6MB each</span>
                                            : null}
                                    </div>
                                    <label className="adminFileButton">
                                        {product.imagesData?.length ? 'Add more photos' : 'Choose photos'}
                                        <input
                                            key={imageKey}
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp,image/gif"
                                            multiple
                                            onChange={onImages}
                                            required={!product.imagesData?.length}
                                        />
                                    </label>
                                </div>
                                <div className="adminProductFields">
                                    <label>
                                        Title
                                        <input
                                            type="text"
                                            value={product.title}
                                            onChange={(event) => updateProduct('title', event.target.value)}
                                            required
                                        />
                                    </label>
                                    <label>
                                        Year
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            maxLength="4"
                                            placeholder="2026"
                                            value={product.year}
                                            onChange={(event) => updateProduct('year', event.target.value)}
                                        />
                                    </label>
                                    <label>
                                        Category
                                        <select
                                            value={product.category}
                                            onChange={(event) => updateProduct('category', event.target.value)}
                                        >
                                            {PRODUCT_CATEGORIES.map((name) => (
                                                <option key={name} value={name}>{name}</option>
                                            ))}
                                        </select>
                                    </label>
                                    <label>
                                        Format
                                        <input
                                            type="text"
                                            placeholder="sticker, print…"
                                            value={product.format}
                                            onChange={(event) => updateProduct('format', event.target.value)}
                                        />
                                    </label>
                                    <label>
                                        Price
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={product.price}
                                            onChange={(event) => updateProduct('price', event.target.value)}
                                            required
                                        />
                                    </label>
                                    <label>
                                        Stock
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            value={product.stock}
                                            onChange={(event) => updateProduct('stock', event.target.value)}
                                        />
                                    </label>
                                    <label>
                                        Shop order
                                        <input
                                            type="number"
                                            step="1"
                                            placeholder="Lower first"
                                            value={product.shopOrder}
                                            onChange={(event) => updateProduct('shopOrder', event.target.value)}
                                        />
                                    </label>
                                    {String(product.format).toLowerCase() !== 'sticker' ?
                                        <label>
                                            Edition
                                            <input
                                                type="text"
                                                placeholder="Edition of 12"
                                                value={product.edition}
                                                onChange={(event) => updateProduct('edition', event.target.value)}
                                            />
                                        </label>
                                        : null}
                                    <label className="adminCheck">
                                        <input
                                            type="checkbox"
                                            checked={Boolean(product.bestSeller)}
                                            onChange={(event) => updateProduct('bestSeller', event.target.checked)}
                                        />
                                        Best seller
                                    </label>
                                    <label className="adminProductWide">
                                        Details
                                        <input
                                            type="text"
                                            placeholder={'two-color screenprint, 12" x 16"'}
                                            value={product.detail}
                                            onChange={(event) => updateProduct('detail', event.target.value)}
                                        />
                                    </label>
                                    <label className="adminProductWide">
                                        Description
                                        <textarea
                                            rows="3"
                                            value={product.summary}
                                            onChange={(event) => updateProduct('summary', event.target.value)}
                                        />
                                    </label>
                                </div>
                            </div>
                            <div className="adminProductActions">
                                <button className="adminButton" type="submit" disabled={adding}>
                                    {adding ? 'Adding…' : 'Add product'}
                                </button>
                                {added ?
                                    <p className="adminSaved">
                                        Added.{' '}
                                        <Link to={added}>View product page</Link>
                                    </p>
                                    : null}
                            </div>
                        </form>

                        <form className="adminPanel adminCatalog" onSubmit={onSave}>
                            <div className="adminPanelHead">
                                <div>
                                    <h2 className="adminSectionTitle">Shop inventory</h2>
                                    <p className="adminSectionCopy">
                                        {liveCount} in the shop
                                        {soldCount ? ` · ${soldCount} sold out` : ''}
                                        . Lowest shop order appears first.
                                    </p>
                                </div>
                                <div className="adminToolbarActions">
                                    {saved ? <p className="adminSaved">Saved. Live on the shop.</p> : null}
                                    <button className="adminButton" type="submit" disabled={saving}>
                                        {saving ? 'Saving…' : 'Save changes'}
                                    </button>
                                </div>
                            </div>
                            <div className="adminInventory">
                                {rows.map(({ item, form }) => {
                                    const { name } = pieceName(item, { shop: true });
                                    const held = Number(reserved?.[item.id] || 0);
                                    const isSticker = item.format === 'sticker';
                                    return (
                                        <article
                                            key={item.id}
                                            className={item.hidden ? 'adminItem adminItemHidden' : 'adminItem'}
                                        >
                                            <Link className="adminItemPhoto" to={`/products/${item.id}`}>
                                                <img src={item.image} alt="" />
                                            </Link>
                                            <div className="adminItemBody">
                                                <header className="adminItemHead">
                                                    <div>
                                                        <h3 className="adminItemTitle">{name}</h3>
                                                        <p className="adminItemMeta">
                                                            {item.category}
                                                            {item.custom ? ' · Added' : ''}
                                                        </p>
                                                    </div>
                                                    <div className="adminItemBadges">
                                                        {item.hidden ? <span className="adminBadge adminBadgeMute">Removed</span> : null}
                                                        {!item.hidden && Number(form.stock) <= 0 ? <span className="adminBadge">Sold out</span> : null}
                                                        {held > 0 ? <span className="adminBadge adminBadgeHold">{held} in carts</span> : null}
                                                    </div>
                                                </header>
                                                <div className="adminItemFields">
                                                    <label>
                                                        Order
                                                        <input
                                                            type="number"
                                                            step="1"
                                                            value={form.shopOrder}
                                                            onChange={(event) => updateRow(item.id, 'shopOrder', event.target.value)}
                                                        />
                                                    </label>
                                                    <label>
                                                        Price
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="0.01"
                                                            value={form.price}
                                                            onChange={(event) => updateRow(item.id, 'price', event.target.value)}
                                                        />
                                                    </label>
                                                    <label>
                                                        Stock
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="1"
                                                            value={form.stock}
                                                            onChange={(event) => updateRow(item.id, 'stock', event.target.value)}
                                                        />
                                                    </label>
                                                    <label>
                                                        Edition
                                                        {isSticker ?
                                                            <span className="adminMuted">None for stickers</span>
                                                            :
                                                            <input
                                                                type="text"
                                                                value={form.edition}
                                                                onChange={(event) => updateRow(item.id, 'edition', event.target.value)}
                                                            />
                                                        }
                                                    </label>
                                                    <label className="adminCheck">
                                                        <input
                                                            type="checkbox"
                                                            checked={Boolean(form.bestSeller)}
                                                            onChange={(event) => updateRow(item.id, 'bestSeller', event.target.checked)}
                                                        />
                                                        Best seller
                                                    </label>
                                                </div>
                                            </div>
                                            {item.hidden ?
                                                <button
                                                    className="adminGhostButton"
                                                    type="button"
                                                    onClick={() => onRestore(item)}
                                                >
                                                    Restore
                                                </button>
                                                :
                                                <button
                                                    className="adminDangerButton"
                                                    type="button"
                                                    onClick={() => onDelete(item)}
                                                >
                                                    Remove
                                                </button>
                                            }
                                        </article>
                                    );
                                })}
                            </div>
                            <p className="adminHint">
                                Stock 0 marks a piece sold out. Remove takes it off the shop; pieces you added are deleted, original pieces can be restored.
                            </p>
                        </form>
                    </>
                    : null}
            </main>
        </div>
    );
};

export default Admin;
